import { TranslateService } from '@ngx-translate/core';
import { CaseStatus } from '../../enums/case-status.enum';
import { UserStatus } from '../../enums/user-status.enum';

export class StatusUtils {
  private static translateService: TranslateService;
  static async initializeTranslateService(service: TranslateService) {
    this.translateService = service;
  }

  private static readonly STATUS_TRANSLATION_KEYS: { [key: number]: string } = {
    [CaseStatus.AIAnalysingError]: 'errorAIAnalysingDesc',
    [CaseStatus.Open]: 'open',
    [CaseStatus.AIAnalysing]: 'AIAnalysing',
    [CaseStatus.AIAnalysingComplete]: 'AIAnalysingComplete',
    [CaseStatus.WaitingConsultation]: 'waitingConsultation',
    [CaseStatus.ReportSent]: 'reportIssued',
    [CaseStatus.QuestionAsk]: 'questionAsked',
    [CaseStatus.QuestionAnswer]: 'questionAnswer',
    [CaseStatus.Closed]: 'closed',
  };

  private static readonly USER_TRANSLATION_KEYS: { [key: string]: string } = {
    [UserStatus.NewAccount]: 'newAccount',
    [UserStatus.Active]: 'active',
    [UserStatus.DeActive]: 'deActive',
    [UserStatus.Blocked]: 'blocked',
    [UserStatus.Deleted]: 'deleted',
    [UserStatus.None]: 'none',
  };

  // Case Status Details
  private static readonly CASE_STATUS_DETAILS: {
    [key in CaseStatus]: { class: string; text: string };
  } = {
    [CaseStatus.AIAnalysingError]: {
      class: 'badge-danger',
      text: 'errorAIAnalysingDesc',
    },
    [CaseStatus.Open]: { class: 'badge-light-primary', text: 'open' },
    [CaseStatus.AIAnalysing]: {
      class: 'badge-light-dark',
      text: 'AIAnalysing',
    },
    [CaseStatus.AIAnalysingComplete]: {
      class: 'badge-light-success',
      text: 'AIAnalysingComplete',
    },
    [CaseStatus.WaitingConsultation]: {
      class: 'badge-light-warning',
      text: 'waitingConsultation',
    },
    [CaseStatus.ReportSent]: {
      class: 'badge-light-info',
      text: 'reportIssued',
    },
    [CaseStatus.QuestionAsk]: {
      class: 'badge-light-body',
      text: 'questionAsked',
    },
    [CaseStatus.QuestionAnswer]: {
      class: 'badge-light-secondary',
      text: 'questionAnswer',
    },
    [CaseStatus.Closed]: { class: 'badge-light-danger', text: 'closed' },
  };

  // Case Status Methods
  static getCaseStatusClass(status: CaseStatus): string {
    return this.CASE_STATUS_DETAILS[status]?.class;
  }

  static getCaseStatusText(status: CaseStatus): string {
    const translationKey = this.STATUS_TRANSLATION_KEYS[status];
    if (translationKey && this.translateService) {
      return this.translateService.instant(translationKey);
    }
    return 'Unknown Status';
  }

  // User Status Details
  private static readonly USER_STATUS_DETAILS: {
    [key in UserStatus]: { class: string; text: string };
  } = {
    [UserStatus.None]: { class: 'badge-light-default', text: 'none' },
    [UserStatus.NewAccount]: {
      class: 'badge-light-primary',
      text: 'newAccount',
    },
    [UserStatus.Active]: { class: 'badge-light-success', text: 'active' },
    [UserStatus.DeActive]: { class: 'badge-light-danger', text: 'deActive' },
    [UserStatus.Blocked]: { class: 'badge-light-secondary', text: 'blocked' },
    [UserStatus.Deleted]: { class: 'badge-light-default', text: 'deleted' },
  };

  // User Status Methods
  static getUserStatusClass(status: UserStatus): string {
    return this.USER_STATUS_DETAILS[status]?.class;
  }

  static getUserStatusText(status: string): string {
    const translationKey = this.USER_TRANSLATION_KEYS[status];
    if (translationKey && this.translateService) {
      return this.translateService.instant(translationKey);
    }
    return 'Unknown Status';
  }

  // Filter Case Statuses
  private static readonly FILTER_CASE_STATUSES: {
    [key in CaseStatus]?: { id: string; title: string };
  } = {
    [CaseStatus.Open]: { id: '1', title: 'Open' },
    [CaseStatus.AIAnalysing]: {
      id: '2',
      title: 'AI Analysing',
    },
    [CaseStatus.AIAnalysingComplete]: {
      id: '3',
      title: 'AI Analysing Complete',
    },
    [CaseStatus.WaitingConsultation]: {
      id: '4',
      title: 'Waiting Consultation',
    },
    [CaseStatus.ReportSent]: { id: '5', title: 'Report Issued' },
    [CaseStatus.QuestionAsk]: { id: '6', title: 'Question Ask' },
    [CaseStatus.QuestionAnswer]: { id: '7', title: 'Question Answer' },
    [CaseStatus.Closed]: { id: '8', title: 'Closed' },
  };

  static getFilterCaseStatuses(): Array<{ id: string; title: string }> {
    return Object.values(this.FILTER_CASE_STATUSES);
  }

  // Filter Worklist Statuses
  private static readonly WORKLIST_CASE_STATUSES: {
    [key in CaseStatus]?: { id: string; title: string };
  } = {
    [CaseStatus.Open]: { id: '1', title: 'Waiting' },
    [CaseStatus.AIAnalysing]: {
      id: '2',
      title: 'Completed',
    },
  };

  static getFilterWorkStatuses(): Array<{ id: string; title: string }> {
    return Object.values(this.WORKLIST_CASE_STATUSES);
  }
}
